import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Filter, Droplets, Leaf, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import DealerLogos from './DealerLogos';
import UnifiedContactModal from './UnifiedContactModal';

const IrrigationFiltration = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const benefits = [
    {
      icon: Filter,
      title: 'Superior Filtration',
      description: 'Remove sediment, algae, and contaminants from irrigation water'
    },
    {
      icon: Droplets,
      title: 'System Protection',
      description: 'Prevent clogging of drip emitters and sprinkler nozzles'
    },
    {
      icon: Leaf,
      title: 'Crop Health',
      description: 'Deliver clean water for optimal plant growth and health'
    }
  ];

  return (
    <section id="irrigation-filtration" className={`${isMobile ? 'py-10' : 'py-20'} bg-gradient-to-br from-ctc-silver-light via-white to-ctc-silver-light`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`text-center ${isMobile ? 'mb-8' : 'mb-16'}`}
        >
          <Link to="/filtration">
            <h2 className={`${isMobile ? 'text-3xl' : 'text-4xl lg:text-5xl'} font-bold text-gray-900 ${isMobile ? 'mb-3' : 'mb-6'} hover:text-ctc-blue transition-colors cursor-pointer`}>
              Filtration
            </h2>
          </Link>
          <p className={`${isMobile ? 'text-base' : 'text-xl'} text-gray-600 max-w-3xl mx-auto`}>
            Protect your irrigation investment with filtration systems. 
            Clean water means healthier crops and longer-lasting equipment.
          </p>
        </motion.div>

        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center ${isMobile ? 'mb-8' : 'mb-16'}`}>
          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div>
              <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-gray-900 ${isMobile ? 'mb-2' : 'mb-4'}`}>
                Clean Water, Better Results
              </h3>
              <p className={`text-gray-600 ${isMobile ? 'mb-3 text-sm' : 'mb-6'}`}>
                Our irrigation filtration systems ensure your crops receive the cleanest 
                possible water while protecting your valuable irrigation equipment from damage.
              </p>
            </div>

            <div className={`${isMobile ? 'space-y-3' : 'space-y-6'}`}>
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-start space-x-4"
                >
                  <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} bg-gradient-to-br from-ctc-blue-light to-ctc-blue rounded-lg flex items-center justify-center flex-shrink-0 shadow-md`}>
                    <benefit.icon className="text-white" size={isMobile ? 20 : 24} />
                  </div>
                  <div>
                    <h4 className={`${isMobile ? 'text-base' : 'text-xl'} font-semibold text-gray-900 ${isMobile ? 'mb-1' : 'mb-2'}`}>
                      {benefit.title}
                    </h4>
                    <p className={`text-gray-600 ${isMobile ? 'text-sm' : ''}`}>
                      {benefit.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className={`bg-gradient-to-br from-ctc-silver-light via-white to-ctc-silver rounded-2xl ${isMobile ? 'p-4' : 'p-8'} shadow-xl`}>
              <img
                src="/BIG Bulk.jpeg"
                alt="CTC Waterworks BIG Series bulk filtration system - industrial scale inline pressure filters for high-capacity applications"
                className={`w-full ${isMobile ? 'h-48' : 'h-80'} object-cover rounded-xl shadow-lg`}
              />
            </div>

            {/* Efficiency Badge */}
            <div className={`absolute -bottom-4 -left-4 bg-white rounded-xl shadow-xl ${isMobile ? 'p-2' : 'p-4'} border border-ctc-silver`}>
              <div className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-ctc-blue`}>99.5%</div>
              <div className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-sm'}`}>Filtration Rate</div>
            </div>
          </motion.div>
        </div>

        {/* Filter Types */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`bg-white rounded-2xl ${isMobile ? 'p-4' : 'p-8'} ${isMobile ? 'mb-8' : 'mb-12'} shadow-lg border border-ctc-silver`}
        >
          <h3 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold text-gray-900 ${isMobile ? 'mb-3' : 'mb-6'} text-center`}>
            Complete Filtration Solutions
          </h3>
          <div className={`grid grid-cols-1 md:grid-cols-3 ${isMobile ? 'gap-3' : 'gap-6'}`}>
            {[
              {
                name: 'Automated Brush & Vacuum Inline Filters',
                description: 'Self-cleaning inline filtration with automated brush and vacuum systems',
                applications: 'High-volume operations, continuous flow'
              },
              {
                name: 'Self Cleaning Suction Screens',
                description: 'Automated suction screen cleaning for intake protection',
                applications: 'Surface water intake, pump protection'
              },
              {
                name: 'Custom Engineered Applications',
                description: 'Tailored filtration solutions for unique requirements',
                applications: 'Specialized crops, unique water sources'
              }
            ].map((filter, index) => (
              <div key={index} className={`bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-lg ${isMobile ? 'p-3' : 'p-6'} border border-ctc-blue/20`}>
                <h4 className={`${isMobile ? 'text-base' : 'text-xl'} font-semibold text-gray-900 ${isMobile ? 'mb-1' : 'mb-3'}`}>{filter.name}</h4>
                <p className={`text-gray-600 ${isMobile ? 'mb-1 text-sm' : 'mb-3'}`}>{filter.description}</p>
                <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-ctc-blue font-medium`}>
                  Best for: {filter.applications}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Dealer Logos */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <DealerLogos showIrrigationDealers={true} />
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`text-center bg-gradient-to-r from-ctc-blue via-ctc-blue-dark to-black rounded-2xl ${isMobile ? 'p-6' : 'p-12'} text-white shadow-2xl`}
        >
          <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold ${isMobile ? 'mb-2' : 'mb-4'}`}>
            Protect Your Irrigation Investment
          </h3>
          <p className={`${isMobile ? 'text-base' : 'text-xl'} ${isMobile ? 'mb-4' : 'mb-8'} opacity-90`}>
            Clean water leads to healthier crops and longer equipment life.
          </p>
          <motion.button
            onClick={() => setIsContactModalOpen(true)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-ctc-blue px-8 py-4 rounded-lg font-semibold hover:bg-ctc-silver-light transition-colors inline-flex items-center space-x-2 shadow-lg"
          >
            <span>Design My Filtration System</span>
            <ArrowRight size={20} />
          </motion.button>
        </motion.div>
      </div>

      {/* Unified Contact Modal */}
      <UnifiedContactModal 
        isOpen={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="product_quote"
      />
    </section>
  );
};

export default IrrigationFiltration;